import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { Microphone, X, Stop } from '@phosphor-icons/react';
import { Brain, X as XIcon } from 'lucide-react';
import { api } from '@/lib/api';
import { LivingBubble } from '@/components/LivingBubble';

const ConversationOrb = ({ bubble, open, onClose, onBubbleSay }) => {
  const [recording, setRecording] = useState(false);
  const [thinking, setThinking] = useState(false);
  const [turns, setTurns] = useState([]);
  const [memChips, setMemChips] = useState([]);
  const [session] = useState(() => Math.random().toString(36).slice(2));

  useEffect(() => {
    if (!open || !bubble?.id) return;
    api.get(`/bubbles/${bubble.id}/conversation`).then(({ data }) => setTurns(data.turns || [])).catch(() => {});
  }, [open, bubble?.id]);

  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const orbRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setTurns([]);
      setMemChips([]);
      try { mediaRef.current && mediaRef.current.stop(); } catch {}
    }
  }, [open]);

  const startRecord = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        const arr = new Uint8Array(await blob.arrayBuffer());
        const b64 = btoa(String.fromCharCode(...arr));
        send(b64);
      };
      mr.start();
      mediaRef.current = mr;
      setRecording(true);
    } catch (e) { toast.error('mic permission needed'); }
  };

  const stopRecord = () => { try { mediaRef.current?.stop(); } catch {}; setRecording(false); };

  const send = async (audio_b64) => {
    setThinking(true);
    try {
      const { data } = await api.post(`/bubbles/${bubble.id}/converse`,
                                       { audio_b64, session_id: session });
      setTurns((t) => [...t, { by: 'user', text: data.user_text },
                              { by: 'bubble', text: data.bubble_text }]);
      if (data.suggested_memories?.length) {
        setMemChips((prev) => [...prev, ...data.suggested_memories]);
      }
      if (data.audio_b64) {
        const a = new Audio(`data:audio/mp3;base64,${data.audio_b64}`);
        a.play().catch(() => {});
        onBubbleSay && onBubbleSay(data.bubble_text);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "couldn't hear");
    } finally { setThinking(false); }
  };

  const dismissChip = (id) => {
    setMemChips((prev) => prev.filter((c) => c.id !== id));
    api.delete(`/memories/${id}`).catch(() => {});
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[200] bg-black/95 backdrop-blur-xl grid place-items-center px-4"
        data-testid="conversation-orb"
      >
        <button onClick={onClose} data-testid="conv-close"
                className="absolute top-5 right-5 p-2 rounded-full glass-hi text-white/70 hover:text-white">
          <X size={20} weight="bold" />
        </button>

        <div className="text-center w-full max-w-lg">
          <div className="text-[10px] uppercase tracking-[0.4em] text-bx-cyan mb-3">
            you are talking to {bubble.name}
          </div>

          <div ref={orbRef} className="relative mx-auto h-72 w-72 sm:h-96 sm:w-96 mb-6">
            <span className={`absolute inset-0 rounded-full blur-3xl opacity-40 ${recording ? 'bg-bx-cyan' : 'bg-bx-magenta'} animate-pulse`} />
            <div className="absolute inset-0">
              <LivingBubble
                bubble={bubble}
                mood={thinking ? 'curious' : recording ? 'excited' : 'happy'}
                className="h-full w-full"
                testid="conv-living-bubble"
              />
            </div>
            <div className="absolute inset-6 sm:inset-10 rounded-full flex items-end justify-center overflow-hidden pointer-events-none">
              <div className="text-center w-full max-h-full overflow-hidden pb-2">
                <AnimatePresence>
                  {turns.slice(-4).map((t, i) => (
                    <motion.div key={`${i}-${t.text}`}
                      initial={{ y: 8, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
                      className={`text-xs sm:text-sm font-bold italic mb-1 px-3 ${
                        t.by === 'user' ? 'text-bx-cyan' : 'text-bx-magenta'
                      }`}>
                      {t.by === 'user' ? 'you' : bubble.name}: <span className="text-white/90 font-normal">"{t.text}"</span>
                    </motion.div>
                  ))}
                </AnimatePresence>
                {thinking && <div className="text-xs text-white/60 animate-pulse">thinking…</div>}
              </div>
            </div>
          </div>

          {/* Remember this? chips */}
          <AnimatePresence>
            {memChips.length > 0 && (
              <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                          className="flex flex-wrap gap-2 justify-center mb-4" data-testid="memory-chips">
                {memChips.map((chip) => (
                  <motion.div key={chip.id} layout initial={{ scale: 0.8 }} animate={{ scale: 1 }}
                              exit={{ scale: 0.8, opacity: 0 }}
                              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-bx-cyan/15 border border-bx-cyan/30 text-xs text-bx-cyan">
                    <Brain size={11} />
                    <span className="max-w-[180px] truncate">{chip.fact}</span>
                    <button onClick={() => dismissChip(chip.id)} data-testid={`chip-dismiss-${chip.id}`}
                            className="text-white/40 hover:text-red-400 ml-0.5">
                      <XIcon size={11} />
                    </button>
                  </motion.div>
                ))}
              </motion.div>
            )}
          </AnimatePresence>

          <button
            data-testid="conv-mic"
            disabled={thinking}
            onMouseDown={!recording ? startRecord : undefined}
            onMouseUp={recording ? stopRecord : undefined}
            onMouseLeave={recording ? stopRecord : undefined}
            onTouchStart={(e) => { e.preventDefault(); if (!recording) startRecord(); }}
            onTouchEnd={(e) => { e.preventDefault(); if (recording) stopRecord(); }}
            className={`relative h-20 w-20 rounded-full grid place-items-center mx-auto transition-all select-none ${
              recording ? 'bg-bx-magenta scale-110' : 'bg-bx-cyan hover:scale-110'
            } text-black disabled:opacity-50`}
            style={recording ? { boxShadow: '0 0 32px rgba(255,0,127,0.6)' } : {}}
          >
            {recording
              ? <><Stop size={28} weight="fill" /><span className="absolute inset-0 rounded-full bg-bx-magenta animate-ping opacity-40" /></>
              : <Microphone size={28} weight="fill" />
            }
          </button>
          <div className="mt-3 text-[10px] uppercase tracking-widest text-white/60">
            {recording ? 'release to send' : thinking ? 'thinking…' : 'hold to speak'}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ConversationOrb;
